/**
 * Device Type Style Registry - 每個 device type 可有多種 modal style
 * Direct cutover: style 先統一走 trait 組合 + CSS contract
 * Naming rule: types-{deviceType}-{styleId}（如 types-ac_unit-style01）
 */
import type { ComponentType } from 'react'
import type { Device } from '../../services/turingMcpApi'

export type DeviceTypeModalProps = {
  device: Device
  deviceState?: Record<string, unknown>
  onCommand: (cmd: string, params?: Record<string, unknown>) => void | Promise<void>
  onClose: () => void
  /** overlay=全螢幕 runtime，preview=402pt 預覽 */
  wrapMode?: 'overlay' | 'preview'
}

const DEVICE_TYPE_STYLE_REGISTRY: Record<string, Record<string, ComponentType<DeviceTypeModalProps>>> = {}

const DEFAULT_STYLE_IDS = ['Style01']

export const DEVICE_TYPE_STYLE_IDS: Record<string, string[]> = {
  ac_unit: ['Style02'],
  hvac: ['Style02'], // hvac 與 ac_unit 共用
  blinds: ['Style01', 'Style02'],
  curtain: ['Style01', 'Style02'],
}

/** Direct cutover: 所有 style 先走 trait 組合；回傳 null 表示使用共用組合渲染路徑 */
export const TRAIT_COMBO_STYLE_ID = 'Style01'

const STYLE_DISPLAY_NAMES: Record<string, string> = {
  Style01: 'Trait combination',
  Style02: 'Trait combination (clone)',
}

/** 轉為 canonical styleId：types-{deviceType}-{styleId}（如 types-ac_unit-style01） */
export function toCanonicalDeviceTypeStyleId(deviceType: string, rawStyleId: string): string {
  const dt = String(deviceType || '').trim().toLowerCase()
  const sid = String(rawStyleId || '').trim().toLowerCase()
  return `types-${dt}-${sid}`
}

/** 從 canonical styleId 解析出 rawStyleId（用於 component 查詢） */
export function parseRawStyleIdFromCanonical(canonicalStyleId: string): string {
  const s = String(canonicalStyleId || '').trim()
  if (!s.startsWith('types-')) return s
  const parts = s.split('-')
  const last = parts[parts.length - 1]
  return last ? last.charAt(0).toUpperCase() + last.slice(1) : s
}

/** 從 canonical styleId 解析出 deviceType（如 types-ac_unit-style01 -> ac_unit） */
export function parseDeviceTypeFromCanonicalStyleId(canonicalStyleId: string): string {
  const s = String(canonicalStyleId || '').trim()
  if (!s.startsWith('types-')) return ''
  const lastDash = s.lastIndexOf('-')
  if (lastDash <= 6) return ''
  return s.slice(6, lastDash).toLowerCase()
}

export function listDeviceTypeStyles(deviceType: string): Array<{ styleId: string; rawStyleId: string; name: string }> {
  const key = deviceType?.toLowerCase()?.trim() || ''
  const ids = DEVICE_TYPE_STYLE_IDS[key] ?? DEFAULT_STYLE_IDS
  return ids.map((rawStyleId) => ({
    styleId: toCanonicalDeviceTypeStyleId(deviceType, rawStyleId),
    rawStyleId,
    name: STYLE_DISPLAY_NAMES[rawStyleId] ?? `${deviceType} ${rawStyleId}`,
  }))
}

export function getDeviceTypeStyleComponent(
  deviceType: string,
  styleId: string
): ComponentType<DeviceTypeModalProps> | null {
  const key = deviceType?.toLowerCase()?.trim() || ''
  const rawStyleId = styleId.startsWith('types-') ? parseRawStyleIdFromCanonical(styleId) : styleId?.trim() || ''
  const map = DEVICE_TYPE_STYLE_REGISTRY[key]
  if (!map) return null
  return map[rawStyleId] ?? null
}

export function hasDeviceTypeStyles(deviceType: string): boolean {
  const key = deviceType?.toLowerCase()?.trim() || ''
  const ids = DEVICE_TYPE_STYLE_IDS[key] ?? DEFAULT_STYLE_IDS
  return ids.length > 0
}

/** 取得 Clone 的目標 styleId（下一個 style slot），若無則回傳 null */
export function getNextCloneTargetStyleId(
  deviceType: string,
  sourceStyleId: string
): string | null {
  const styles = listDeviceTypeStyles(deviceType)
  const idx = styles.findIndex((s) => s.styleId === sourceStyleId)
  if (idx < 0 || idx >= styles.length - 1) return null
  return styles[idx + 1].styleId
}
